import { inject, NgModule } from '@angular/core';
import { RedirectFunction, Router, RouterModule, Routes } from '@angular/router';
import { HomeComponent } from './home/home.component';
import { ParcelDescriptionComponent } from './parcel-description/parcel-description.component';
import { AuthComponent } from './features/auth/auth.component';
import { PhoneAuthComponent } from './features/auth/phone-auth/phone-auth.component';
import { CompleteProfileComponent } from './features/auth/complete-profile/complete-profile.component';
import { AccountRecoveryComponent } from './features/auth/account-recovery/account-recovery.component';
import { FooterComponent } from './footer/footer.component';
// HistoryComponent — migrated to portal module
import { ResetPasswordComponent } from './features/auth/reset-password/reset-password.component';
// UserProfileComponent — migrated to portal module
// TrackingComponent / ParcelDetailsComponent lazy-loaded via TrackModule sous /track
// (Phase 3) — voir { path: 'track', loadChildren } + redirects ci-dessous
import { ParcelListComponent } from './parcel-list/parcel-list.component';
import { SuccessMessageSentComponent } from './features/info/pages/success-message-sent/success-message-sent.component';
// Pages statiques (conditions, privacy, legal, litiges, fraud, cookies, faq,
// contactus) lazy-loaded via InfoModule sous /info (Phase 6) — voir
// `{ path: 'info', loadChildren }` + redirects back-compat ci-dessous
// Ship pages (new-shipment, order-summary, thank-you, price-calculator, tarifs,
// pickup-points, heavy-parcel-*) routed lazily via ShipModule — see terminal
// `{ path: '', loadChildren: ShipModule }` below (Phase 2-C)
import { ActivateAccountComponent } from './features/auth/activate-account/activate-account.component';
import { BackofficeLoginComponent } from './features/auth/backoffice-login/backoffice-login.component';
import { Verify2FAComponent } from './features/auth/backoffice-login/verify-2fa/verify-2fa.component';
import { Setup2FAComponent } from './features/auth/backoffice-login/setup-2fa/setup-2fa.component';
// OrderDetailsComponent — migrated to portal module
import { ManagerOrderDetailsComponent } from './order-details/manager-order-details.component';
// MyReceiversComponent — migrated to portal module
import { StaffAuthGuard } from './core/guards/staff-auth.guard';
import { TeamOnlyGuard } from './core/guards/team-only.guard';
import { AuthenticatedGuard } from './core/guards/authenticated.guard';
import { ManagerGuard } from './core/guards/manager.guard';
import { TeamLoginComponent } from './features/auth/team-login/team-login.component';
import { UnauthorizedComponent } from './features/auth/unauthorized/unauthorized.component';
import { AssistedOrderComponent } from './features/assisted/assisted-order.component';

/**
 * Redirection qui PRÉSERVE les query params et le fragment de l'URL entrante.
 *
 * ⚠️ C'est la SEULE forme admise dans ce fichier (verrou :
 * `legacy-client-redirects.spec.ts`). Un `redirectTo` écrit en **chaîne** perd
 * silencieusement les query params et le fragment : `createQueryParams`
 * (`@angular/router`) n'itère que sur les paramètres écrits DANS la cible de
 * redirection, jamais sur ceux de l'URL entrante. Le dépôt a déjà payé cette
 * régression deux fois — `/details?trackingID=…` (incident dev 2026-07-06,
 * « Code invalide » systématique) puis `/order-details?trackingID=…` (#1139,
 * écran de détail vide). Une redirection sans paramètre AUJOURD'HUI en aura
 * un demain : on n'arbitre pas au cas par cas, on préserve toujours.
 *
 * Exporté pour être exercé par le spec.
 */
export function preserveParams(target: string): RedirectFunction {
  return ({ queryParams, fragment }) =>
    inject(Router).createUrlTree([target], { queryParams, fragment: fragment ?? undefined });
}

// Exporte pour le spec de garde app-routing-404.spec.ts (invariant : le `**`
// doit rester la derniere route — cf. #652).
export const routes: Routes = [
    // 🔒 TEAM ACCESS CONTROL ROUTES (pas de guard ici)
    {path: 'team-login', component: TeamLoginComponent},
    {path: 'unauthorized', component: UnauthorizedComponent},

    // 🏠 PUBLIC ROUTES (protégées par TeamOnlyGuard si requireTeamAuth = true)
    // Home reste eager + pathMatch:'full' pour ne matcher QUE l'URL vide —
    // les paths ship non-vides tombent dans le parent lazy `{ path: '' }` terminal
    {path: '', component: HomeComponent, pathMatch: 'full', canActivate: [TeamOnlyGuard]},
    // #1904 (partie « promotions » de #1902) — les anciens écrans staff des
    // promotions sont redirigés vers l'écran du back-office : les règles
    // `promotions` exigent désormais `updatedBy` (et `deletedBy` à la
    // suppression), que seul `MarketingService` écrit. Le nouvel écran couvre
    // tous leurs gestes (créer, modifier, pause/réactivation, supprimer,
    // filtres, code généré) ; son garde est `StaffAuthGuard` (FINANCE_ROLES).
    // Les liens existants (dashboard.service) restent valables par ces redirections.
    {path: 'promotions-list', redirectTo: preserveParams('/backoffice/promotions'), pathMatch: 'full'},
    {path: 'promotion', redirectTo: preserveParams('/backoffice/promotions'), pathMatch: 'full'},
    {path: 'home', component: HomeComponent, canActivate: [TeamOnlyGuard]},
    // 📊 Dashboard - Accessible aux utilisateurs avec isEmployee + ability manager/topManager
    {path: 'dashboard', component: ParcelDescriptionComponent, canActivate: [ManagerGuard]},
    // 📊 PROTECTED: Parcel Operations - Managers (isEmployee + ability)
    // #1902 lot 1 — `/manageEmployee`, `/createEmployee`, `/status`, `/scan` et
    // `/heavy-parcel-processing/:id` retirés (back-office : annuaire, préparation,
    // `backoffice/heavy-processing/:id`). Restent `/dashboard` → `/parcel-list` →
    // `/manager-order-details`, seul chemin de correction du poids déclaré tant
    // que #1913 n'est pas livré.
    {path: 'parcel-list', component: ParcelListComponent, canActivate: [ManagerGuard]},
    {path: 'auth', component: AuthComponent, canActivate: [TeamOnlyGuard]},
    {path: 'auth/phone', component: PhoneAuthComponent, canActivate: [TeamOnlyGuard]},
    // 🎯 #331-B5 : guard authentifié simple (PAS TeamOnlyGuard) — un compte
    // phone-auth n'a pas d'email sur son token avant complete-profile, la
    // whitelist teamEmails le rejetait donc en dev/acc (redirect /team-login).
    {path: 'auth/complete-profile', component: CompleteProfileComponent, canActivate: [AuthenticatedGuard]},
    // 🔓 AUCUN garde — récupération de compte. Même raison que
    // `reset-password` ci-dessous : ces pages s'adressent par définition à un
    // utilisateur déconnecté, et `TeamOnlyGuard` exige une session Firebase.
    {path: 'auth/account-recovery', component: AccountRecoveryComponent},
    {path: 'footer', component: FooterComponent, canActivate: [TeamOnlyGuard]},
    // 🔗 REDIRECTIONS PERMANENTES vers MyBefret (#1139). Ces quatre URLs plates
    // sont parties dans de vrais e-mails clients avant la migration des écrans
    // vers le portail : elles ne doivent JAMAIS casser, et ne sont donc jamais
    // supprimées (garde-fou : `legacy-client-redirects.spec.ts`).
    //
    // ⚠️ `redirectTo` en FONCTION (`preserveParams`), jamais en string : un
    // `redirectTo` string PERD les queryParams (même régression que /details,
    // cf. plus bas). `/order-details` était appelée avec `?trackingID=…` et
    // l'écran cible du portail lit `params['trackingID']`
    // (order-details.component.ts:48) — en string, un client cliquant sur son
    // ancien lien d'e-mail atterrissait sur un écran de détail SANS colis. Les
    // trois autres n'ont pas de paramètre connu aujourd'hui, mais la forme
    // fonction est adoptée uniformément : c'est la seule qui reste correcte si
    // un paramètre apparaît un jour.
    {path: 'history', redirectTo: preserveParams('/portal/shipments'), pathMatch: 'full'},
    {path: 'my-receivers', redirectTo: preserveParams('/portal/address-book'), pathMatch: 'full'},
    {path: 'order-details', redirectTo: preserveParams('/portal/shipments/detail'), pathMatch: 'full'},
    // 📊 PROTECTED: Manager Order Details - Managers (isEmployee + ability)
    {path: 'manager-order-details', component: ManagerOrderDetailsComponent, canActivate: [ManagerGuard]},
    // 🔓 AUCUN garde — mot de passe oublié. `TeamOnlyGuard` exige une session
    // Firebase (`auth.currentUser`) : garder cette page rendait la
    // réinitialisation IMPOSSIBLE en dev/acceptance, où `requireTeamAuth` vaut
    // `true` — il fallait être connecté pour atteindre l'écran qui sert à
    // récupérer sa connexion. Constaté le 2026-07-29 (manager bloqué sur
    // dev.befret.be et test.befret.be). En production le garde était déjà
    // inopérant (`requireTeamAuth: false`), donc ce retrait n'y change rien.
    // Ces deux pages n'exposent aucune donnée : elles appellent
    // `sendPasswordResetEmail`. Même arbitrage que #331-B5 pour
    // `auth/complete-profile`.
    {path: 'reset-password', component: ResetPasswordComponent},
    // 🔗 Redirection permanente vers MyBefret (#1139) — cf. le bloc `history` /
    // `my-receivers` / `order-details` ci-dessus (forme fonction obligatoire).
    {path: 'userprofile', redirectTo: preserveParams('/portal/profile'), pathMatch: 'full'},
    {path: 'sucess-message', component: SuccessMessageSentComponent, canActivate: [TeamOnlyGuard]},
    // 📍 TRACK (Lazy Loaded — suivi colis) sous /track. Composants standalone
    // (migration étape 1) → routes lazy `TRACK_ROUTES` (plus de NgModule). Guard
    // team-only porté par le parent (tracking + details étaient gated TeamOnlyGuard).
    {path: 'track', loadChildren: () => import('./features/track/track.routes').then(m => m.TRACK_ROUTES), canActivate: [TeamOnlyGuard]},
    // Redirects back-compat depuis les anciennes URLs plates.
    // ⚠️ redirectTo en FONCTION (pas en string) : un redirectTo string PERD les
    // queryParams — régression de la migration lazy /track (Phase 3 #47) : tous
    // les appelants historiques (home, tracking, payment, VM dashboard, emails)
    // naviguent vers /details?trackingID=… et atterrissaient sur /track/details
    // SANS paramètre → « Code invalide » systématique (incident dev 2026-07-06,
    // BF-BE02-667398-1).
    {path: 'tracking', redirectTo: preserveParams('/track/tracking'), pathMatch: 'full'},
    {path: 'details', redirectTo: preserveParams('/track/details'), pathMatch: 'full'},
    // 📄 INFO (Lazy Loaded — pages statiques : légal, FAQ, contact) sous /info.
    // Composants standalone (migration étape 0) → routes lazy `INFO_ROUTES` (plus de NgModule).
    // Guard team-only porté par le parent (toutes ces pages étaient gated TeamOnlyGuard).
    {path: 'info', loadChildren: () => import('./features/info/info.routes').then(m => m.INFO_ROUTES), canActivate: [TeamOnlyGuard]},
    // Redirects back-compat depuis les anciennes URLs plates → /info/*
    // (#1160) Forme fonction ici aussi : ces URLs sont dans des e-mails partis
    // depuis des années (`/conditions` est le pied de page de ~60 templates),
    // et rien ne garantit qu'aucune ne portera jamais d'ancre ou de paramètre.
    {path: 'conditions', redirectTo: preserveParams('/info/conditions'), pathMatch: 'full'},
    {path: 'privacy', redirectTo: preserveParams('/info/privacy'), pathMatch: 'full'},
    {path: 'legal', redirectTo: preserveParams('/info/legal'), pathMatch: 'full'},
    {path: 'litiges', redirectTo: preserveParams('/info/litiges'), pathMatch: 'full'},
    {path: 'fraud', redirectTo: preserveParams('/info/fraud'), pathMatch: 'full'},
    {path: 'cookies', redirectTo: preserveParams('/info/cookies'), pathMatch: 'full'},
    {path: 'faq', redirectTo: preserveParams('/info/faq'), pathMatch: 'full'},
    {path: 'contactus', redirectTo: preserveParams('/info/contactus'), pathMatch: 'full'},
    // 📱 ASSISTED ORDER (Envoi Assisté — lien reçu par SMS, avec `?code=…`)
    // 🔒 #1173 : verrou de PRÉ-PRODUCTION. « Public » ici veut dire « sans compte
    // client » (le code du SMS fait foi), pas « ouvert à tout internet » : en
    // dev/acceptance cet écran mène à un vrai paiement Stripe (montants ÷10 en
    // acceptance). `TeamOnlyGuard` est inerte en production (`requireTeamAuth:
    // false`), donc le parcours client réel n'est pas touché.
    // Le `?code=…` survit à la redirection : `TeamOnlyGuard` met `state.url`
    // (chemin + query) dans `returnUrl`, et `/team-login` le rejoue via
    // `navigateByUrl` (cf. team-login.component.ts).
    {path: 'ma-commande', component: AssistedOrderComponent, canActivate: [TeamOnlyGuard]},
    // 📧 DÉSINSCRIPTION EMAILS MARKETING (#565 — public, lien reçu par email,
    // pas de guard : le destinataire n'a pas forcément de compte)
    {path: 'unsubscribe', loadComponent: () => import('./features/info/pages/unsubscribe/unsubscribe.component').then(m => m.UnsubscribeComponent)},
    // Route legacy → redirige vers le backoffice customer service
    {path: 'envoi-assiste', redirectTo: preserveParams('/backoffice/envoi-assiste'), pathMatch: 'full'},
    // 📦 SHIP / parcours d'envoi — routes déplacées dans ShipRoutingModule (lazy),
    // captées par le parent `{ path: '', loadChildren: ShipModule }` en fin de tableau
    // 🌐 UNIFIED PORTAL (Lazy Loaded — clients B2B/B2C + partenaires livraison/marketing)
    // Composants standalone (migration étape 3) → routes lazy `PORTAL_ROUTES` (plus de NgModule).
    // 🔒 #1173 : `TeamOnlyGuard` au PARENT — il s'exécute donc AVANT le
    // `PortalAuthGuard` porté par la route enfant (Angular évalue les gardes de
    // haut en bas de l'arbre, parent d'abord). Composition voulue :
    //  - non authentifié en dev/acc → `/team-login?returnUrl=<url complète>`,
    //    et `PortalAuthGuard` n'est jamais atteint ;
    //  - authentifié hors équipe en dev/acc → `/unauthorized`. C'est le verrou de
    //    pré-production, PAS le défaut corrigé par #1137 (celui-ci visait un
    //    client légitime sans rôle résolu, en production) ;
    //  - en production le garde retourne `true` immédiatement
    //    (`requireTeamAuth: false`) : `PortalAuthGuard` garde intégralement son
    //    comportement, repli client #1137 compris.
    // Les deux gardes construisent leur `returnUrl` à partir de `state.url`
    // (#1140 PR1), qui porte chemin + query + fragment : la destination survit.
    {path: 'portal', loadChildren: () => import('./portal/portal.routes').then(m => m.PORTAL_ROUTES), canActivate: [TeamOnlyGuard]},
    // 🤝 PARTNER PORTAL — Legacy redirect → unified portal (#1160 : forme
    // fonction, l'ancienne string perdait queryParams et fragment).
    //
    // ⚠️ `pathMatch: 'prefix'` : le RESTE du chemin (`/partner/xyz`) n'est PAS
    // reporté sur la cible, et c'est VOULU. Ce n'est d'ailleurs pas un
    // changement — une redirection absolue (string commençant par `/` comme
    // fonction rendant un `UrlTree`) lève `AbsoluteRedirect` dans
    // `applyRedirectCommands`, ce qui court-circuite le
    // `newSegments.concat(remainingSegments)` de
    // `expandSegmentAgainstRouteUsingRedirect` : les segments restants étaient
    // déjà jetés avant ce correctif.
    //
    // Les reporter serait une RÉGRESSION : les sous-chemins de l'ancien portail
    // partenaire n'ont pas d'équivalent sous `/portal` — `/partner/login`, qui
    // part encore par e-mail aux partenaires invités (`invite-partner.ts`),
    // deviendrait `/portal/login`, une 404. `/portal` sans suffixe atterrit sur
    // `DashboardRouterComponent`, qui aiguille selon le rôle : c'est la bonne
    // cible pour n'importe quel `/partner/*`.
    {path: 'partner', redirectTo: preserveParams('/portal'), pathMatch: 'prefix'},
    // 📦 VIRTUAL MAILBOX (Lazy Loaded) — composants standalone (migration étape 2)
    // → routes lazy `VM_ROUTES` (plus de NgModule).
    {path: 'vm', loadChildren: () => import('./vm/vm.routes').then(m => m.VM_ROUTES), canActivate: [TeamOnlyGuard]},
    // 🏭 BACKOFFICE LOGISTIQUE (Lazy Loaded)
    {path: 'backoffice', loadChildren: () => import('./backoffice/backoffice.routes').then(m => m.BACKOFFICE_ROUTES), canActivate: [StaffAuthGuard], data: { roles: ['admin', 'rh', 'warehouse', 'transitory', 'manager', 'topManager', 'delivery', 'customs', 'support'] }}, // #2050 support
    // 👤 ACTIVATION COMPTE EMPLOYÉ (public - lien reçu par email)
    {path: 'activate-account', component: ActivateAccountComponent},
    // 🔐 CONNEXION EMPLOYÉ (page dédiée)
    {path: 'backoffice-login', component: BackofficeLoginComponent},
    // 🔐 2FA — Vérification et configuration obligatoire
    {path: 'verify-2fa', component: Verify2FAComponent},
    {path: 'setup-2fa', component: Setup2FAComponent},
    // 📦 SHIP MODULE (Lazy Loaded — parcours d'envoi). Parent empty-path TERMINAL :
    // conserve les URLs ship top-level (/new-shipment, /order-summary, /thank-you,
    // /tarifs, /pickup-points, /heavy-*…). DOIT rester en dernier — prefix match sur ''
    // capte tout path non consommé par les routes nommées ci-dessus. Composants
    // standalone (migration étape 5) → routes lazy `SHIP_ROUTES` (plus de NgModule).
    //
    // 🔒 #1173 : `TeamOnlyGuard` posé ICI, au parent. Les pages du tunnel le
    // portaient déjà une par une dans `SHIP_ROUTES` ; le garde parent supprime la
    // dépendance à cette discipline — une page ajoutée demain à `SHIP_ROUTES` sans
    // garde ne rouvre plus le money path en dev/acceptance (où les montants Stripe
    // sont divisés par 10 : un inconnu paierait réellement).
    // ⚠️ Un `canActivate` de parent n'intervient PAS dans le matching : les URLs
    // plates (/new-shipment, /order-summary, /thank-you…) sont résolues exactement
    // comme avant, l'ordre des routes nommées ci-dessus est inchangé, et une URL
    // inconnue backtrack toujours jusqu'au `**` (les gardes ne s'exécutent
    // qu'après la reconnaissance de route). Vérifié en navigation réelle, cf.
    // `team-only-preprod-coverage.spec.ts`.
    // ⚠️ Conséquence assumée : les 5 pages `heavy-quote-*` / `heavy-payment-success`
    // de `SHIP_ROUTES`, atteintes depuis un e-mail et volontairement sans garde,
    // deviennent elles aussi team-only en dev/acceptance — c'est la décision
    // « tout ce qui est en pré-prod est réservé à l'équipe ». Le destinataire de
    // ces e-mails en pré-prod est un testeur, et son lien (avec ses query params)
    // est rejoué après `/team-login`. En PRODUCTION rien ne change :
    // `requireTeamAuth: false` ⇒ le garde retourne `true` immédiatement.
    {path: '', loadChildren: () => import('./features/ship/ship.routes').then(m => m.SHIP_ROUTES), canActivate: [TeamOnlyGuard]},
    // 🚧 404 — DOIT rester la toute dernière route (#652). Sans wildcard, une URL
    // inconnue levait `NG04002` en console et la navigation échouait : l'utilisateur
    // restait sur la home sans message. Le prefix-match du parent ship ci-dessus ne
    // capte pas ces paths (aucun enfant ne matche) → Angular backtrack jusqu'ici.
    {path: '**', loadComponent: () => import('./features/info/pages/not-found/not-found.component').then(m => m.NotFoundComponent)},
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    scrollPositionRestoration: 'top',
    anchorScrolling: 'enabled'
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
